// react
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  useLocation,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// material ui icons
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';

// material ui
import { Card, Stack, Tooltip } from '@mui/material';

// api
import type { ReadGatewayDto } from '@/api/generated/recalc/endpoints/v1/gateways';
import { useGatewaysGetAsyncByidQuery } from '@/api/generated/recalc/endpoints/v1/gateways';

// shared components
import { WritableOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import LocationMap from '@/components/maps/LocationMap';
import { EditButtonLink } from '@/components/ui/button';
import { DeleteButton } from '@/components/ui/button/DeleteButton';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import DeviceTabs from '@/features/devices/components/shared/DeviceTabs';
import DeleteGatewayDialog from '@/features/gateways/components/DeleteGatewayDialog';
import GatewayDetailHeader from '@/features/gateways/components/GatewayDetailHeader';
import GatewayImageSection from '@/features/gateways/components/GatewayImageSection';
import SensorActivitiesDataGrid from '@/features/gateways/components/SensorActivitiesDataGrid';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

import { skipToken } from '@reduxjs/toolkit/query';

const GATEWAY_TAB = {
  SENSORS: 0,
  MAP: 1,
  IMAGE: 2,
} as const;

type GatewayTabValue = (typeof GATEWAY_TAB)[keyof typeof GATEWAY_TAB];
type GatewayRouteState = Partial<Pick<ReadGatewayDto, 'GatewayId'>>;

export const Route = createFileRoute('/$accountId/_user/gateways/$gatewayId/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('units-gateways-sensors');
  const { gatewayId } = useParams({ strict: false });
  const navigate = useNavigate();
  const location = useLocation();
  const selectedAccount = useSelector(getSelectedAccount);
  const prevAccountIdRef = React.useRef(selectedAccount?.AccountId);

  const gatewayState = (location.state ?? {}) as GatewayRouteState;
  const gatewayIdFromState = gatewayState.GatewayId;
  const parsedGatewayIdFromParam = gatewayId ? Number(gatewayId) : undefined;
  const resolvedGatewayId = gatewayIdFromState ?? parsedGatewayIdFromParam;
  const queryGatewayId =
    typeof resolvedGatewayId === 'number' && Number.isFinite(resolvedGatewayId)
      ? resolvedGatewayId
      : skipToken;

  const { data: gateway, isLoading } = useGatewaysGetAsyncByidQuery(
    queryGatewayId === skipToken ? skipToken : { id: queryGatewayId },
  );

  const [activeTab, setActiveTab] = useState<GatewayTabValue>(
    GATEWAY_TAB.SENSORS,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const hasLocation = gateway?.Latitude != null && gateway?.Longitude != null;
  const hasImage = gateway?.ImageHash != null;

  // Handle account changes - only useEffect for side effects
  useEffect(() => {
    const curr = selectedAccount?.AccountId;
    const prev = prevAccountIdRef.current;
    if (prev != null && curr != null && prev !== curr) {
      navigate({ to: '/$accountId/gateways' });
    }
    if (curr != null) prevAccountIdRef.current = curr;
  }, [selectedAccount?.AccountId, navigate]);

  if (!gateway) return null;

  return (
    <PageContainer data-testid="gateway-detail-container">
      <GatewayLoadingIndicator isLoading={isLoading} />
      <PageHeader title={t('gateways.gateway')}>
        <WritableOnly>
          <Tooltip title={t('gateways.editGateway', 'Edit Gateway')}>
            <EditButtonLink to="edit">
              {t('common:actions.edit', 'Edit')}
            </EditButtonLink>
          </Tooltip>
          <Tooltip
            title={t('gateways.deleteGateway', {
              name: gateway?.Name || gateway?.GatewayId || '',
            })}
          >
            <DeleteButton onClick={() => setDeleteDialogOpen(true)}>
              {t('common:actions.delete', 'Delete')}
            </DeleteButton>
          </Tooltip>
        </WritableOnly>
      </PageHeader>

      <GatewayDetailHeader key={gateway.GatewayId} gateway={gateway} />
      <Stack spacing={2} my={2}>
        <DeviceTabs
          activeTab={activeTab}
          onTabChange={(_, newValue) => {
            if (typeof newValue === 'number') {
              setActiveTab(newValue as GatewayTabValue);
            }
          }}
          tabs={[
            {
              label: t(
                'gateways.gatewayActivity',
                'Sensor Activities (last 24h)',
              ),
              testId: 'gateway-tab-sensors',
              icon: <SensorsRoundedIcon fontSize="small" />,
            },
            {
              label: t('gateways.gatewayLocation', 'Location'),
              testId: 'gateway-tab-map',
              disabled: !hasLocation,
              icon: <LocationOnRoundedIcon fontSize="small" />,
            },
            {
              label: t('gateways.gatewayImage', 'Image'),
              testId: 'gateway-tab-image',
              disabled: !hasImage,
              icon: <ImageRoundedIcon fontSize="small" />,
            },
          ]}
        />

        {activeTab === GATEWAY_TAB.SENSORS && (
          <SensorActivitiesDataGrid
            sensorActivities={gateway.SensorActivities}
            externalSensorActivities={gateway.ExternalSensorActivities}
          />
        )}
        {activeTab === GATEWAY_TAB.MAP && hasLocation && (
          <Card variant="outlined">
            <LocationMap
              pins={[
                {
                  latitude: gateway.Latitude,
                  longitude: gateway.Longitude,
                  label: gateway.Name,
                },
              ]}
              height="40vh"
              zoom={16}
            />
          </Card>
        )}

        {activeTab === GATEWAY_TAB.IMAGE && hasImage && (
          <Card variant="outlined">
            <GatewayImageSection gateway={gateway} t={t} />
          </Card>
        )}
      </Stack>

      <DeleteGatewayDialog
        open={deleteDialogOpen}
        gateway={gateway ?? null}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => navigate({ to: '/$accountId/gateways' })}
      />
    </PageContainer>
  );
}
